import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowDownToLine, ArrowUpFromLine, Megaphone, AlertTriangle, Info } from 'lucide-react';
import { useLocale } from '@/i18n/LocaleContext';
import { SectionHeading } from './SectionHeading';

export const Tokenomics = () => {
  const { t } = useLocale();
  const reduced = useReducedMotion();
  const tk = t.tokenomics;

  const rise = (delay = 0) => ({
    initial: reduced ? {} : { opacity: 0, y: 28 },
    whileInView: reduced ? {} : { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-80px' },
    transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <section id="tokenomics" data-testid="tokenomics-section" className="relative py-20 sm:py-28 border-t-2 border-border overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: 'url(/assets/04-backgrounds/gold-bars-pattern-seamless.svg)', backgroundSize: '380px' }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading testId="tokenomics-heading" label={tk.label} title={tk.title} />

        {/* Giant tax plates */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-8 lg:gap-12">
          <motion.div {...rise(0)}>
            <div
              data-testid="tokenomics-buy-card"
              className="sticker-frame relative rounded-3xl bg-primary p-8 sm:p-10 rotate-[-1.5deg] hover:rotate-0 transition-transform duration-300"
            >
              <div className="flex items-center justify-between">
                <p className="font-display text-lg sm:text-xl font-bold uppercase tracking-widest text-primary-foreground/80">
                  {tk.buyTaxLabel}
                </p>
                <span className="flex h-12 w-12 items-center justify-center rounded-full border-[3px] border-ink bg-background">
                  <ArrowDownToLine className="h-6 w-6 text-primary" aria-hidden="true" />
                </span>
              </div>
              <p className="font-display mt-2 text-7xl sm:text-8xl font-bold leading-none text-primary-foreground">
                {tk.buyTaxValue}
              </p>
              <div className="mt-5 h-2 w-24 rounded-full bg-ink/80" aria-hidden="true" />
            </div>
          </motion.div>
          <motion.div {...rise(0.08)}>
            <div
              data-testid="tokenomics-sell-card"
              className="sticker-frame relative rounded-3xl bg-accent p-8 sm:p-10 rotate-[1.5deg] hover:rotate-0 transition-transform duration-300"
            >
              <div className="flex items-center justify-between">
                <p className="font-display text-lg sm:text-xl font-bold uppercase tracking-widest text-accent-foreground/80">
                  {tk.sellTaxLabel}
                </p>
                <span className="flex h-12 w-12 items-center justify-center rounded-full border-[3px] border-ink bg-background">
                  <ArrowUpFromLine className="h-6 w-6 text-accent" aria-hidden="true" />
                </span>
              </div>
              <p className="font-display mt-2 text-7xl sm:text-8xl font-bold leading-none text-accent-foreground">
                {tk.sellTaxValue}
              </p>
              <div className="mt-5 h-2 w-24 rounded-full bg-ink/60" aria-hidden="true" />
            </div>
          </motion.div>
        </div>

        {/* 50 / 50 split showcase */}
        <motion.div {...rise(0.1)} className="mt-20">
          <div className="flex items-center gap-5">
            <h3 className="font-display text-xl sm:text-2xl font-bold text-foreground">{tk.splitTitle}</h3>
            <span aria-hidden="true" className="font-display text-2xl sm:text-3xl font-bold text-primary rotate-[-2deg] inline-block">
              50 / 50
            </span>
          </div>

          <div className="mt-8 grid lg:grid-cols-[1fr_minmax(300px,460px)_1fr] gap-8 lg:gap-6 items-stretch">
            <motion.div {...rise(0.05)} className="flex">
              <div
                data-testid="tokenomics-creator-card"
                className="sticker-frame w-full rounded-3xl bg-primary p-7 sm:p-8 rotate-[-1.2deg] hover:rotate-0 transition-transform duration-300 flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full border-[3px] border-ink bg-background">
                    <Megaphone className="h-6 w-6 text-primary" aria-hidden="true" />
                  </span>
                  <p className="font-display text-5xl sm:text-6xl font-bold text-primary-foreground leading-none">50%</p>
                </div>
                <p className="font-display mt-5 text-xl font-bold text-primary-foreground">{tk.creatorFundLabel}</p>
                <p className="mt-1 text-sm font-semibold text-primary-foreground/80">{tk.creatorFundValue}</p>
                <p className="mt-3 text-sm leading-relaxed text-primary-foreground/80">{tk.creatorFundBody}</p>
                <div className="mt-auto pt-5">
                  <div className="h-2 w-20 rounded-full bg-ink/80" aria-hidden="true" />
                </div>
              </div>
            </motion.div>

            <motion.div {...rise(0.1)} className="relative self-center w-full">
              <div className="hero-frame rounded-2xl overflow-hidden bg-ink">
                <img
                  src="/assets/05-tokenomics/tax-split-visual.png"
                  alt={tk.visualAlt}
                  data-testid="tokenomics-split-visual"
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </div>
              <img
                src="/assets/01-mascot/cutouts/peipei-pointing-flat.png"
                alt=""
                aria-hidden="true"
                className="gentle-float absolute -bottom-12 -left-6 sm:-left-12 w-28 sm:w-36 drop-shadow-xl"
                loading="lazy"
              />
            </motion.div>

            <motion.div {...rise(0.15)} className="flex">
              <div
                data-testid="tokenomics-dividends-card"
                className="sticker-frame w-full rounded-3xl bg-gold p-7 sm:p-8 rotate-[1.2deg] hover:rotate-0 transition-transform duration-300 flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <img src="/assets/04-props/xaut-gold-bar.png" alt="" aria-hidden="true" className="h-12 w-auto drop-shadow" loading="lazy" />
                  <p className="font-display text-5xl sm:text-6xl font-bold text-ink leading-none">50%</p>
                </div>
                <p className="font-display mt-5 text-xl font-bold text-ink">{tk.dividendsLabel}</p>
                <p className="mt-1 text-sm font-semibold text-ink/80">{tk.dividendsValue}</p>
                <p className="mt-3 text-sm leading-relaxed text-ink/80">{tk.dividendsBody}</p>
                <div className="mt-auto pt-5">
                  <div className="h-2 w-20 rounded-full bg-ink/70" aria-hidden="true" />
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Qualifiers */}
        <motion.div {...rise(0.12)} className="mt-16 grid md:grid-cols-2 gap-5">
          <div
            data-testid="tokenomics-clarifier"
            className="flex items-start gap-3 rounded-xl border-2 border-primary/60 bg-secondary px-5 py-4"
          >
            <AlertTriangle className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm sm:text-base font-semibold text-foreground leading-relaxed">{tk.clarifier}</p>
          </div>
          <div className="flex items-start gap-3 rounded-xl border border-border bg-card px-5 py-4">
            <Info className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
            <div className="text-sm leading-relaxed text-muted-foreground">
              <p>{tk.dependency}</p>
              <p className="mt-2">{tk.flowNote}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
